import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { ChevronLeft, ChevronRight } from "lucide-react";

const ACTION_COLORS = {
    USER_BLOCKED: "bg-red-500/10 text-red-600",
    USER_UNBLOCKED: "bg-emerald-500/10 text-emerald-600",
    USER_DELETED: "bg-red-500/10 text-red-600",
    USER_REACTIVATED: "bg-emerald-500/10 text-emerald-600",
    USER_DEACTIVATED: "bg-amber-500/10 text-amber-700",
    USER_UPDATED: "bg-ink/5 text-ink",
    USER_PASSWORD_RESET: "bg-planet-sun/20 text-planet-sun",
};

export default function AdminAudit() {
    const [entries, setEntries] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(50);
    const [filterAdmin, setFilterAdmin] = useState("");
    const [filterAction, setFilterAction] = useState("");

    const load = useCallback(async () => {
        const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
        if (filterAdmin) params.set("admin_email", filterAdmin);
        if (filterAction) params.set("action", filterAction);
        const r = await api.get(`/admin/audit?${params}`);
        setEntries(r.data.entries);
        setTotal(r.data.total);
    }, [page, pageSize, filterAdmin, filterAction]);

    useEffect(() => { load(); }, [load]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <AdminLayout title="Audit Log" subtitle={`${total} admin actions logged`}>
            <div className="flex flex-wrap gap-3 mb-4">
                <input className="sp-input flex-1 min-w-[220px]" placeholder="Filter by admin email…" value={filterAdmin} onChange={(e) => { setFilterAdmin(e.target.value); setPage(1); }} data-testid="admin-audit-filter-admin" />
                <input className="sp-input flex-1 min-w-[220px]" placeholder="Filter by action…" value={filterAction} onChange={(e) => { setFilterAction(e.target.value); setPage(1); }} data-testid="admin-audit-filter-action" />
            </div>

            <div className="bg-surface border border-ink/10 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-ink/10 text-xs text-ink-muted uppercase tracking-wider">
                                <th className="text-left px-3 py-3">When</th>
                                <th className="text-left px-3 py-3">Admin</th>
                                <th className="text-left px-3 py-3">Action</th>
                                <th className="text-left px-3 py-3">Affected User</th>
                                <th className="text-left px-3 py-3">IP</th>
                                <th className="text-left px-3 py-3">Details</th>
                            </tr>
                        </thead>
                        <tbody data-testid="audit-tbody">
                            {entries.map((e) => (
                                <tr key={e.audit_id} className="border-b border-ink/5 align-top">
                                    <td className="px-3 py-3 text-xs text-ink-muted whitespace-nowrap">{(e.created_at || "").slice(0, 19).replace("T", " ")}</td>
                                    <td className="px-3 py-3">{e.admin_email}</td>
                                    <td className="px-3 py-3">
                                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${ACTION_COLORS[e.action] || "bg-ink/5 text-ink"}`}>{e.action}</span>
                                    </td>
                                    <td className="px-3 py-3 text-xs">{e.affected_user_email || e.affected_user_id || "—"}</td>
                                    <td className="px-3 py-3 text-xs font-mono text-ink-muted">{e.ip_address || "—"}</td>
                                    <td className="px-3 py-3 text-xs font-mono text-ink-muted max-w-[300px] truncate">
                                        {e.details && Object.keys(e.details).length ? JSON.stringify(e.details) : "—"}
                                    </td>
                                </tr>
                            ))}
                            {entries.length === 0 && (
                                <tr><td colSpan={6} className="px-3 py-10 text-center text-ink-muted">No audit entries yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-t border-ink/10 text-xs text-ink-muted">
                    <span>Page {page} of {totalPages}</span>
                    <div className="flex items-center gap-1">
                        <button className="p-1.5 hover:bg-ink/10 rounded disabled:opacity-40" disabled={page === 1} onClick={() => setPage((p) => p - 1)} data-testid="audit-prev">
                            <ChevronLeft strokeWidth={1.5} className="w-4 h-4" />
                        </button>
                        <button className="p-1.5 hover:bg-ink/10 rounded disabled:opacity-40" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="audit-next">
                            <ChevronRight strokeWidth={1.5} className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}
